import React, {  useEffect, useState, Fragment } from 'react';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import deepEqual from 'fast-deep-equal';
import styled from 'styled-components/macro';

import Page, { Back, Title, PageButton } from '../../../components/Page';
import DiscountTypeSelector from '../../campaignsCreation/components/DiscountTypeSelector';
import { Success, SaveEdit, DateTimePickersWrapper, DateTimePicker, EditWrapper } from '../../common/components';
import { Campaign, DiscountToEdit, DiscountType, LocationType, AssetRequestObject } from '../../types';
import { State as DiscountEditState } from '../reducer';
import { LoadingOverlay } from '../../../components/Loading';
import useHelpers from '../../common/useHelpers';
import { Discount as DiscountBackendType } from '../../../types/discounts';
import {
    getLimitationByDiscountType,
    getRequirementsByDiscountType,
    getRewardByDiscountType
} from '../../campaignsCreation/helpers';
import LoyaltyDiscount from '../../campaignsCreation/components/LoyaltyDiscount/LoyaltyDiscount';
import Promotion from '../../campaignsCreation/components/Promotion/Promotion';
import Location from '../../campaignsCreation/containers/Location';
import Card from '../../../components/Card';
import DeleteDiscountModal from './DeleteDiscountModal';
import { distances } from '../../../styles/constants';
import { validate } from '../../validation/discountEditValidation';
import { ValidationState } from '../../../components/Forms';
import { parseDiscountToFrontendType } from '../../common/helpers';
import Icon from '../../../components/Icons';
import { useScopes } from '../../../auth/accessToken/Allow';
import { P } from '../../../components/Typography';
import { checkIsMccOnly, getStoreIds } from '../../helpers';

interface DiscountEditProps {
    discountEditState: DiscountEditState;
    fetchDiscountCustomers: (accountId: string, discountId: string) => void;
    fetchDiscount: (accountId: string, discountId: string) => void;
    clearFetchedData: () => void;
    updateDiscount: (accountId: string, discount: DiscountBackendType) => void;
    deleteDiscount: (accountId: string, discountId: string) => void;
    removeDiscountImageAsset: () => void;
    createAsset: (accountId: string, asset: AssetRequestObject) => void;
}

interface DiscountEditValues {
    discounts: DiscountToEdit[];
    locationType: LocationType;
    storeIds: string[];
    mccs: string[];
    mccRanges: [string, string][];
}

enum Stage {
    'edit',
    'success',
}

const getInitialLocationType = (discount: DiscountBackendType | undefined, isMccOnly: boolean): LocationType => {
    if (isMccOnly) {
        return LocationType.mcc;
    }

    if (
        (discount?.requirement.store?.mcc || []).length > 0 ||
        (discount?.requirement.store?.mcc_ranges || []).length > 0
    ) {
        return LocationType.mcc;
    }

    const locationType =
        discount?.requirement?.store_ids &&
        discount?.requirement?.store_ids.length > 0 &&
        discount?.requirement?.store_ids[0] !== '*'
            ? LocationType.predefinedLocations
            : LocationType.allLocations;
    return locationType;
};

const DiscountEdit = (
    {
        discountEditState,
        fetchDiscount,
        fetchDiscountCustomers,
        clearFetchedData,
        updateDiscount,
        deleteDiscount,
        removeDiscountImageAsset,
        createAsset,
    }: DiscountEditProps
) => {
    const { t } = useTranslation();
    const { accountId, discountId } = useParams();
    const [initialValues, setInitialValues] = useState<DiscountEditValues | undefined>(undefined);
    const [stage, setStage] = useState<Stage>(Stage.edit);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const scopes = useScopes();

    useEffect(() => {
        if (accountId && discountId) {
            fetchDiscount(accountId, discountId);
            fetchDiscountCustomers(accountId, discountId);
        }
        return () => clearFetchedData();
    }, [accountId, discountId, fetchDiscount, fetchDiscountCustomers, clearFetchedData]);

    const { discount } = discountEditState;
    const helpers = useHelpers();
    const isMccOnly = checkIsMccOnly(accountId);

    useEffect(() => {
        if (discount) {
            const newInitialValues = parseDiscountToFrontendType(discount, helpers) as DiscountToEdit;
            if (!deepEqual(newInitialValues, initialValues?.discounts[0])) {
                const locationType = getInitialLocationType(discount, isMccOnly);
                const allStoreIds = getStoreIds(discount);
                const storeIds =
                    allStoreIds && allStoreIds[0] !== '*'
                        ? allStoreIds
                        : [];

                const mccs = discount.requirement.store?.mcc || [];
                const mccRanges = (discount.requirement.store?.mcc_ranges || []).map(({ start, end }) => [
                    start,
                    end,
                ]) as [string, string][];
                setInitialValues({ discounts: [newInitialValues], locationType, storeIds, mccs, mccRanges });
            }
        }
    }, [discount, helpers, initialValues, isMccOnly]);

    const checkIfScheduledOrActive = () => {
        const currentTimestamp = new Date().getTime();
        const dateToTimestamp = new Date(initialValues?.discounts[0].dateTo ?? 0).getTime();
        return currentTimestamp < dateToTimestamp;
    };

    const onSubmit = (values: DiscountEditValues) => {
        if (initialValues && accountId) {
            const editedDiscount = values.discounts[0];
            if (
                !deepEqual(editedDiscount, discount) ||
                !deepEqual(values.storeIds, getStoreIds(discount)) ||
                !deepEqual(values.mccs, initialValues.mccs) ||
                !deepEqual(values.mccRanges, initialValues.mccRanges)
            ) {
                updateDiscount(accountId, getDiscountToOperate(values));
            }
            setStage(Stage.success);
        }
    };

    const getDiscountToOperate = (values: DiscountEditValues) => {
        const discount = values.discounts[0];
        return {
            id: discount.id,
            active: true,
            private: false,
            name: discount.title,
            description: discount.description,
            limitation: getLimitationByDiscountType(discount),
            requirement: getRequirementsByDiscountType(discount, {
                dateFrom: discount.dateFrom,
                dateTo: discount.dateTo,
                timeFrom: discount.timeFrom,
                timeTo: discount.timeTo,
                storeIds: values.storeIds,
                mccs: values.mccs,
                mccRanges: values.mccRanges,
            } as Campaign),
            reward: getRewardByDiscountType(discount),
            links: discountEditState.discount?.links || discount.webshop ? [
                ...(discountEditState.discount?.links?.filter(x => x.rel !== 'webshop') || []),
                ...(discount.webshop ? [{
                    href: discount.webshop,
                    rel: 'webshop',
                }] : []),
            ] : undefined,
            metadata: discount.metadata_dintero_exclude_public_description ? { 'dintero:exclude:public:description': discount.metadata_dintero_exclude_public_description } : undefined,
        };
    };

    if (!initialValues) {
        return <LoadingOverlay />;
    }

    const onDeleteDiscount = () => {
        if (accountId && initialValues.discounts[0].id) {
            deleteDiscount(accountId, initialValues.discounts[0].id);
        }
    };

    if (stage === Stage.success) {
        return <Success subtitle={t('marketing.campaign.success_edit_discount_text')} />;
    }

    const isDiscountCompleted = () => {
        const currentDate = new Date().getTime();
        const toDate = new Date(discount?.requirement.purchase_to ?? '0').getTime();
        return currentDate > toDate;
    };

    const allCustomersAdded =
        discountEditState.discountCustomers &&
        discountEditState.discountCustomers.length === 1 &&
        !discountEditState.discountCustomers[0].customer_id;

    const createAssetRequest = (asset: AssetRequestObject) => {
        if (accountId) {
            createAsset(accountId, asset);
        }
    };

    const asset = ((discountEditState.discount && discountEditState.discount.links) || []).find(
        (x) => x.rel === 'thumbnail_discount_image'
    )?.href;
    return (
        <Page
            title={
                <Fragment>
                    <Back />
                    <Title>{t('marketing.campaign.discount_edit')}</Title>
                    {!isDiscountCompleted() && (
                        <PageButton onClick={() => setShowDeleteModal(true)}>
                            <Icon icon="delete" />
                            {t('marketing.campaign.deactivate_discount')}
                        </PageButton>
                    )}
                </Fragment>
            }
        >
            <CardsWrapper>
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    validate={(values) => validate(values.discounts[0], t)}
                    onSubmit={(values) => onSubmit(values)}
                >
                    {({ values, handleChange, handleBlur, handleSubmit, setFieldValue, touched, errors }) => (
                        <form onSubmit={handleSubmit}>
                            <Card
                                title={`${t('marketing.campaign.discount')} ${
                                    initialValues.discounts[0].title ? `: ${initialValues.discounts[0].title}` : ''
                                }`}
                                shortHr
                                marginBottom={distances.small}
                            >
                                <DateTimePickersWrapper>
                                    <DateTimePicker
                                        value={{
                                            date: values.discounts[0].dateFrom,
                                            time: values.discounts[0].timeFrom,
                                        }}
                                        setDate={(dateFrom) => {
                                            setFieldValue('discounts.0.dateFrom', dateFrom);
                                        }}
                                        setTime={(timeFrom) => {
                                            setFieldValue('discounts.0.timeFrom', timeFrom);
                                        }}
                                        name={'discounts.0.timeFrom'}
                                        label={t('marketing.campaign.active_from')}
                                        isEdit={true}
                                    />
                                    <DateTimePicker
                                        value={{
                                            date: values.discounts[0].dateTo,
                                            time: values.discounts[0].timeTo,
                                        }}
                                        setDate={(dateTo) => {
                                            setFieldValue('discounts.0.dateTo', dateTo);
                                        }}
                                        setTime={(timeTo) => {
                                            setFieldValue('discounts.0.timeTo', timeTo);
                                        }}
                                        name={'discounts.0.timeTo'}
                                        label={t('marketing.campaign.active_to')}
                                        validation={
                                            (errors as any).dateTo
                                                ? {
                                                    state: ValidationState.Invalid,
                                                    message: (errors as any).dateTo as string,
                                                }
                                                : undefined
                                        }
                                        isEdit={
                                            !checkIfScheduledOrActive() ||
                                            !!(discount?.deleted_at && discount.deleted_by)
                                        }
                                    />
                                </DateTimePickersWrapper>
                                <DiscountTypeSelector
                                    discount={values.discounts[0]}
                                    index={0}
                                    setFieldValue={setFieldValue}
                                    isEditable={false}
                                    isMccOnly={isMccOnly}
                                />
                                {values.discounts[0].type === DiscountType.loyalty && (
                                    <LoyaltyDiscount
                                        discount={values.discounts[0]}
                                        index={0}
                                        setFieldValue={setFieldValue}
                                        handleChange={handleChange}
                                        handleBlur={handleBlur}
                                        touched={touched}
                                        errors={errors}
                                        createAsset={createAssetRequest}
                                        removeAsset={removeDiscountImageAsset}
                                        isEditable={false}
                                        isScheduledOrActive={checkIfScheduledOrActive()}
                                        scopes={scopes}
                                        asset={asset}
                                    />
                                )}
                                {values.discounts[0].type !== DiscountType.loyalty && (
                                    <Promotion
                                        discount={values.discounts[0]}
                                        index={0}
                                        setFieldValue={setFieldValue}
                                        handleChange={handleChange}
                                        handleBlur={handleBlur}
                                        touched={touched}
                                        errors={errors}
                                        createAsset={createAssetRequest}
                                        removeAsset={removeDiscountImageAsset}
                                        isEditable={false}
                                        isScheduledOrActive={checkIfScheduledOrActive()}
                                        scopes={scopes}
                                        asset={asset}
                                        isMccOnly={isMccOnly}
                                    />
                                )}
                            </Card>
                            <EditWrapper isEditable={checkIfScheduledOrActive()}>
                                <Location
                                    values={
                                        {
                                            locationType: values.locationType,
                                            storeIds: values.storeIds,
                                            mccs: values.mccs,
                                            mccRanges: values.mccRanges,
                                        } as Campaign
                                    }
                                    setFieldValue={setFieldValue}
                                    handleBlur={handleBlur}
                                    isMccOnly={isMccOnly}
                                />
                            </EditWrapper>
                            {discountEditState.discountCustomers && (
                                <Card
                                    title={t('marketing.discountEdit.customers.title')}
                                    shortHr
                                    marginBottom={distances.tiny}
                                >
                                    {allCustomersAdded && (
                                        <P>{t('marketing.discountEdit.customers.given_to_all_customers')}</P>
                                    )}
                                    {!allCustomersAdded && (
                                        <P>
                                            {t('marketing.discountEdit.customers.given_to', {
                                                count: discountEditState.discountCustomers.length,
                                            })}
                                        </P>
                                    )}
                                </Card>
                            )}
                            {checkIfScheduledOrActive() && <SaveEdit title={t('marketing.campaign.save_campaign')} />}
                        </form>
                    )}
                </Formik>
            </CardsWrapper>
            {showDeleteModal && (
                <DeleteDiscountModal
                    name={initialValues.discounts[0].title}
                    deleteDiscount={() => onDeleteDiscount()}
                    dismissModal={() => setShowDeleteModal(false)}
                />
            )}
        </Page>
    );
};

const CardsWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

export default DiscountEdit;
